/**
 * The fields a payment state is derived from. Structural rather than a generated DTO: each
 * portal generates its own, and they are distinct types that no single signature can accept.
 */
export interface LinkedAccountPaymentFields {
    balance?: number | null;
    nextDueDate?: string | null;
}

/**
 * How a linked account stands with the user, derived rather than reported: the business
 * systems tell us a balance and a due date, not a payment state. `accountStatus` answers a
 * different question (whether the account still exists to be paid), so it is deliberately
 * not folded in here — a closed account with nothing owed reads as paid.
 */
export type PaymentState = 'pastDue' | 'due' | 'paid';

export const PAYMENT_STATES: readonly PaymentState[] = ['pastDue', 'due', 'paid'];

export const PAYMENT_STATE_LABELS: Record<PaymentState, string> = {
    pastDue: 'Past due',
    due: 'Due',
    paid: 'Paid'
};

/** Modifier classes from the global `.status-badge` utility (styles/utilities). */
export const PAYMENT_STATE_BADGE_CLASSES: Record<PaymentState, string> = {
    pastDue: 'status-badge--error',
    due: 'status-badge--warning',
    paid: 'status-badge--success'
};

/**
 * Modifier classes from the global `.status-amount` utility, which tints the balance itself.
 *
 * Shared by the card and the table so one account's balance is tinted the same in both — a
 * settled balance is untinted rather than coloured "safe", so `paid` names no class.
 */
export const PAYMENT_STATE_AMOUNT_CLASSES: Record<PaymentState, string> = {
    pastDue: 'status-amount--error',
    due: 'status-amount--warning',
    paid: ''
};

/**
 * `todayIsoDate` is the tenant's calendar day, not the browser's, and `nextDueDate` arrives
 * as `YYYY-MM-DD` — so a lexicographic comparison is calendar-correct with no date parsing.
 */
export function derivePaymentState(
    account: LinkedAccountPaymentFields,
    todayIsoDate: string
): PaymentState {
    const balance = account.balance ?? 0;

    if (balance <= 0) {
        return 'paid';
    }

    if (account.nextDueDate && account.nextDueDate < todayIsoDate) {
        return 'pastDue';
    }

    return 'due';
}
